import { useFormik } from "formik";
import { useEffect } from "react";
import {
  convertToFilter,
  DEFAULT_LIST_QUERY_PARAMS,
  makeObservable,
  parseFilterFromLocationSearch,
  serializeFilterToQueryParams,
} from "../../../util";

// observable store
const contactFilterStore = makeObservable({});
const CONTACT_PAGINATION_QUERY_PARAM = "p";
const DEFAULT_CONTACT_PAGINATION = { limit: 50, offset: 0 };
const RESERVED_CONTACT_QUERY_PARAMS = [
  ...DEFAULT_LIST_QUERY_PARAMS,
  CONTACT_PAGINATION_QUERY_PARAM,
];

const parseContactPaginationParam = (rawPaginationValue = "") => {
  const [rawOffset, rawLimit] = String(rawPaginationValue).split(",");
  const parsedOffset = Number(rawOffset);
  const parsedLimit = Number(rawLimit);

  if (
    Number.isInteger(parsedOffset) &&
    parsedOffset >= 0 &&
    Number.isInteger(parsedLimit) &&
    parsedLimit > 0
  ) {
    return { limit: parsedLimit, offset: parsedOffset };
  }

  return DEFAULT_CONTACT_PAGINATION;
};

const useContactFilters = () => {
  const formik = useFormik({
    initialValues: {
      address: { operator: "eq" },
      countryId: { operator: "in" },
      county: { operator: "in" },
      email: { operator: "like" },
      exhUserId: { operator: "in" },
      id: { operator: "eq" },
      limit: 50,
      name: { operator: "like" },
      offset: 0,
      salesUser2Id: { operator: "in" },
      salesUserId: { operator: "in" },
      sort1: { operator: "ASC" },
      sort2: { operator: "ASC" },
      sort3: { operator: "ASC" },
      tab: "contact_details",
      town: { operator: "eq" },
      typeId: { operator: "in" },
      _contactArtist: { artistId: { operator: "in" } },
      _contactAttribute: { typeId: { operator: "in" } },
      _contactFocus: { focusId: { operator: "in" } },
      _contactMailingList: { mailingListId: { operator: "in" } },
      _invoice: {
        _item: {
          _instance: {
            _edition: {
              _artwork: {
                _artist: { id: { operator: "in" } },
                categoryId: { operator: "in" },
              },
            },
          },
        },
        analysisId: { operator: "in" },
      },
      _offer: { holdId: { operator: "gt" } },
    },
  });

  const filterKeys = ["contactCategory", "tab"];

  const skipKeys = ["limit", "offset"];

  useEffect(() => {
    // convert formik values to filter and update store
    contactFilterStore.set(
      convertToFilter({ values: formik.values, filterKeys, skipKeys }),
    );
  }, [formik.values]);

  return {
    formik,
  };
};

const getContactSearchFromLocationSearch = locationSearch => {
  return parseFilterFromLocationSearch({
    locationSearch,
    reservedKeys: RESERVED_CONTACT_QUERY_PARAMS,
  });
};

const serializeContactSearchToQueryParams = ({
  currentParams = new URLSearchParams(),
  searchInput,
} = {}) => {
  const src = searchInput ?? {};
  const { limit: _omitLimit, offset: _omitOffset, ...filterOnly } = src;
  return serializeFilterToQueryParams({
    currentParams,
    searchInput: filterOnly,
    reservedKeys: RESERVED_CONTACT_QUERY_PARAMS,
  });
};

const getContactPaginationFromLocationSearch = locationSearch => {
  const searchParams = new URLSearchParams(locationSearch);
  const paginationParam = searchParams.get(CONTACT_PAGINATION_QUERY_PARAM);

  if (!paginationParam) {
    return DEFAULT_CONTACT_PAGINATION;
  }

  return parseContactPaginationParam(paginationParam);
};

export {
  CONTACT_PAGINATION_QUERY_PARAM,
  serializeContactSearchToQueryParams,
  contactFilterStore,
  getContactPaginationFromLocationSearch,
  getContactSearchFromLocationSearch,
  useContactFilters,
};
